'use client'

import { useEffect } from 'react'

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  useEffect(() => {
    // Log the global error with full details
    console.error('Global Error Caught:', {
      name: error.name,
      message: error.message,
      stack: error.stack,
      digest: error.digest,
      fullError: JSON.stringify(error, Object.getOwnPropertyNames(error), 2),
    })

    // Try to send to error logging endpoint
    try {
      fetch('/api/errors', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          errorId: `global_error_${Date.now()}`,
          name: error.name,
          message: error.message,
          stack: error.stack,
          digest: error.digest,
          timestamp: new Date().toISOString(),
          url: typeof window !== 'undefined' ? window.location.href : 'Unknown',
          userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'Unknown',
          environment: process.env.NODE_ENV,
          fullError: JSON.parse(JSON.stringify(error, Object.getOwnPropertyNames(error))),
        })
      }).catch(err => console.error('Failed to log error:', err))
    } catch (e) {
      console.error('Error logging failed:', e)
    }
  }, [error])

  return (
    <html>
      <body style={{ 
        margin: 0, 
        padding: 0, 
        fontFamily: 'system-ui, -apple-system, sans-serif',
        backgroundColor: '#f9fafb',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ 
          maxWidth: '1000px', 
          width: '100%', 
          padding: '2rem',
          margin: '0 auto'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '0.5rem',
            padding: '2rem',
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>😞</div>
            <h2 style={{ 
              fontSize: '1.5rem', 
              fontWeight: 'bold', 
              color: '#111827',
              marginBottom: '0.5rem' 
            }}>
              Critical Application Error
            </h2>
            <p style={{ color: '#6b7280', marginBottom: '1.5rem' }}>
              A critical error occurred at the application root level.
            </p>

            {error.digest && (
              <div style={{
                marginBottom: '1.5rem',
                padding: '0.75rem',
                backgroundColor: '#f3f4f6',
                borderRadius: '0.5rem'
              }}>
                <p style={{ fontSize: '0.875rem', color: '#4b5563' }}>
                  <strong>Error Digest:</strong>{' '}
                  <code style={{ 
                    backgroundColor: '#e5e7eb', 
                    padding: '0.25rem 0.5rem', 
                    borderRadius: '0.25rem' 
                  }}>
                    {error.digest}
                  </code>
                </p>
              </div>
            )}

            {/* Full Error Details */}
            <div style={{ marginBottom: '1.5rem', textAlign: 'left' }}>
              <details open style={{
                backgroundColor: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: '0.5rem',
                padding: '1rem',
                marginBottom: '1rem'
              }}>
                <summary style={{
                  cursor: 'pointer',
                  fontWeight: '600',
                  color: '#7f1d1d',
                  marginBottom: '0.5rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <span>Full Error Details (Production Mode)</span>
                  <button
                    onClick={() => {
                      const errorDetails = {
                        name: error.name,
                        message: error.message,
                        stack: error.stack,
                        digest: error.digest,
                        fullError: JSON.parse(JSON.stringify(error, Object.getOwnPropertyNames(error))),
                      }
                      navigator.clipboard?.writeText(JSON.stringify(errorDetails, null, 2))
                        .then(() => alert('Error details copied to clipboard'))
                        .catch(err => console.error('Failed to copy:', err))
                    }}
                    style={{
                      fontSize: '0.75rem',
                      backgroundColor: '#fee2e2',
                      padding: '0.25rem 0.5rem',
                      borderRadius: '0.25rem',
                      border: 'none',
                      cursor: 'pointer'
                    }}
                    title="Copy error details to clipboard"
                  >
                    📋 Copy
                  </button>
                </summary>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <div>
                    <strong style={{ color: '#991b1b' }}>Error Name:</strong>
                    <pre style={{
                      fontSize: '0.875rem',
                      color: '#b91c1c',
                      marginTop: '0.25rem',
                      padding: '0.5rem',
                      backgroundColor: '#fee2e2',
                      borderRadius: '0.25rem',
                      overflow: 'auto',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word'
                    }}>
                      {error?.name || 'Unknown'}
                    </pre>
                  </div>
                  
                  <div>
                    <strong style={{ color: '#991b1b' }}>Error Message:</strong>
                    <pre style={{
                      fontSize: '0.875rem',
                      color: '#b91c1c',
                      marginTop: '0.25rem',
                      padding: '0.5rem',
                      backgroundColor: '#fee2e2',
                      borderRadius: '0.25rem',
                      overflow: 'auto',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word'
                    }}>
                      {error?.message || 'No error message available'}
                    </pre>
                  </div>
                  
                  {error.digest && (
                    <div>
                      <strong style={{ color: '#991b1b' }}>Error Digest:</strong>
                      <pre style={{
                        fontSize: '0.875rem',
                        color: '#b91c1c',
                        marginTop: '0.25rem',
                        padding: '0.5rem',
                        backgroundColor: '#fee2e2',
                        borderRadius: '0.25rem',
                        overflow: 'auto'
                      }}>
                        {error.digest}
                      </pre>
                    </div>
                  )}
                  
                  <div>
                    <strong style={{ color: '#991b1b' }}>Error Stack:</strong>
                    <pre style={{
                      fontSize: '0.75rem',
                      color: '#b91c1c',
                      marginTop: '0.25rem',
                      padding: '0.5rem',
                      backgroundColor: '#fee2e2',
                      borderRadius: '0.25rem',
                      overflow: 'auto',
                      maxHeight: '15rem',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word'
                    }}>
                      {error?.stack || 'No stack trace available'}
                    </pre>
                  </div>
                  
                  <div>
                    <strong style={{ color: '#991b1b' }}>Full Error Object:</strong>
                    <pre style={{
                      fontSize: '0.75rem',
                      color: '#b91c1c',
                      marginTop: '0.25rem',
                      padding: '0.5rem',
                      backgroundColor: '#fee2e2',
                      borderRadius: '0.25rem',
                      overflow: 'auto',
                      maxHeight: '15rem',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word'
                    }}>
                      {JSON.stringify(error, Object.getOwnPropertyNames(error), 2)}
                    </pre>
                  </div>
                </div>
              </details>

              <details style={{
                backgroundColor: '#eff6ff',
                border: '1px solid #bfdbfe',
                borderRadius: '0.5rem',
                padding: '1rem'
              }}>
                <summary style={{
                  cursor: 'pointer',
                  fontWeight: '600',
                  color: '#1e3a8a',
                  marginBottom: '0.5rem'
                }}>
                  System Information
                </summary>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: '1rem',
                  fontSize: '0.875rem'
                }}>
                  <div>
                    <strong style={{ color: '#1e40af' }}>Timestamp:</strong>
                    <p style={{ color: '#1d4ed8' }}>{new Date().toISOString()}</p>
                  </div>
                  <div>
                    <strong style={{ color: '#1e40af' }}>URL:</strong>
                    <p style={{ color: '#1d4ed8', wordBreak: 'break-all' }}>
                      {typeof window !== 'undefined' ? window.location.href : 'Unknown'}
                    </p>
                  </div>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <strong style={{ color: '#1e40af' }}>User Agent:</strong>
                    <p style={{ color: '#1d4ed8', fontSize: '0.75rem', wordBreak: 'break-all' }}>
                      {typeof navigator !== 'undefined' ? navigator.userAgent : 'Unknown'}
                    </p>
                  </div>
                  <div>
                    <strong style={{ color: '#1e40af' }}>Environment:</strong>
                    <p style={{ color: '#1d4ed8' }}>{process.env.NODE_ENV}</p>
                  </div>
                </div>
              </details>
            </div>

            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '0.75rem',
              justifyContent: 'center',
              marginBottom: '1rem'
            }}>
              <button
                onClick={reset}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: '#f3f4f6',
                  color: '#111827',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem',
                  fontSize: '1rem',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                Try Again
              </button>
              <button
                onClick={() => window.location.href = '/'}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: '#2563eb',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.375rem',
                  fontSize: '1rem',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                Go Home
              </button>
            </div>

            <p style={{ marginTop: '1.5rem', fontSize: '0.875rem', color: '#6b7280' }}>
              If this problem persists, please contact support with the error details above.
            </p>
          </div>
        </div>
      </body>
    </html>
  )
}

